'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/lib/store/useAuthStore';
import { useVoiceSettings } from '@/lib/services/settings';
import { XMPPClientImpl } from '@/lib/libs/xmpp/client';
import {
  getGlobalXMPPClient,
  setGlobalXMPPClient,
  clearGlobalXMPPClient,
} from '@/lib/libs/xmpp/hooks/useXMPP';

/**
 * Componente que inicializa a conexÃ£o XMPP global.
 * Deve ser renderizado uma vez no layout da aplicaÃ§Ã£o.
 * Conecta automaticamente quando o usuÃ¡rio estÃ¡ autenticado e possui configuraÃ§Ãµes XMPP.
 *
 */
export function XMPPInit() {
  const pathname = usePathname();
  const squadToken = useAuthStore((s) => s.squadToken);
  const authStatus = useAuthStore((s) => s.authStatus);
  const userId = squadToken?.user_id;
  const isExternal = squadToken?.isExternal || authStatus === 'external';

  // SÃ³ busca configuraÃ§Ãµes XMPP se o usuÃ¡rio estiver autenticado (nÃ£o externo)
  const { data: voiceSettings, isLoading: isLoadingSettings } = useVoiceSettings({
    enabled: authStatus === 'authenticated' && !isExternal,
  });
  const xmppSettings = isExternal ? squadToken : voiceSettings;
  const xmppDomain = xmppSettings?.xmppDomain;
  const xmppUser = xmppSettings?.xmppUser;
  const xmppPw = xmppSettings?.xmppPw;

  // Ref para armazenar o cliente XMPP
  const xmppClientRef = useRef<XMPPClientImpl | null>(null);
  const isConnectingRef = useRef(false);
  const connectedIdentityRef = useRef<string | null>(null);

  // Contador de tentativas para evitar loops infinitos (reseta apÃ³s sucesso ou mudanÃ§a de rota)
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPathnameRef = useRef<string | null>(null);
  const MAX_RETRIES = 5;
  const RETRY_BASE_DELAY_MS = 2_000;

  // Estado para forÃ§ar re-check de conexÃ£o
  const [connectionCheck, setConnectionCheck] = useState(0);

  // Verifica se precisa reconectar
  const needsConnection = useCallback(() => {
    const globalClient = getGlobalXMPPClient();

    // Se jÃ¡ estÃ¡ conectado, nÃ£o precisa
    if (!isExternal && globalClient && globalClient.status === 'online') {
      return false;
    }

    // Se a ref local estÃ¡ conectada, nÃ£o precisa
    if (xmppClientRef.current && xmppClientRef.current.status === 'online') {
      return false;
    }

    return true;
  }, []);

  // Reseta contador quando muda de rota
  useEffect(() => {
    if (pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = pathname;

      // Se mudou para uma pÃ¡gina de room e nÃ£o estÃ¡ conectado, reseta contador e forÃ§a check
      if (pathname?.includes('/rooms/') && needsConnection()) {
        console.log('[XMPPInit] NavegaÃ§Ã£o para sala detectada, verificando conexÃ£o...');
        retryCountRef.current = 0;
        isConnectingRef.current = false;
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        setConnectionCheck((prev) => prev + 1);
      }
    }
  }, [pathname, needsConnection]);

  // Cleanup separado - executa APENAS no unmount (padrÃ£o do projeto pai)
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (xmppClientRef.current) {
        xmppClientRef.current.stop().catch(console.error);
        xmppClientRef.current = null;
        connectedIdentityRef.current = null;
        clearGlobalXMPPClient();
      }
    };
  }, []);

  // InicializaÃ§Ã£o da conexÃ£o XMPP
  useEffect(() => {
    if (!userId) {
      return;
    }

    if (isLoadingSettings) {
      return;
    }

    if (!xmppUser || !xmppPw || !xmppDomain) {
      return;
    }
    const requestedIdentity = `${xmppUser}@${xmppDomain}`;

    if (isConnectingRef.current) {
      return;
    }

    // Verificar se jÃ¡ existe um cliente global conectado
    const globalClient = getGlobalXMPPClient();
    if (!isExternal && globalClient && globalClient.status === 'online') {
      xmppClientRef.current = globalClient as XMPPClientImpl;
      return;
    }

    // Se a ref local jÃ¡ existe e estÃ¡ online, nÃ£o reconecta
    if (
      xmppClientRef.current &&
      xmppClientRef.current.status === 'online' &&
      (!isExternal || connectedIdentityRef.current === requestedIdentity)
    ) {
      return;
    }

    // Verificar limite de tentativas para evitar loops
    if (retryCountRef.current >= MAX_RETRIES) {
      console.warn('[XMPPInit] Limite de tentativas de reconexÃ£o atingido');
      return;
    }

    const initXMPP = async () => {
      isConnectingRef.current = true;
      retryCountRef.current += 1;

      console.log(
        `[XMPPInit] Iniciando conexÃ£o XMPP (tentativa ${retryCountRef.current}/${MAX_RETRIES})...`,
      );

      try {
        if (isExternal && globalClient) {
          await globalClient.stop();
          clearGlobalXMPPClient();
        }

        // Para externos, uma nova credencial sempre substitui a conexÃ£o anterior.
        if (xmppClientRef.current && (isExternal || xmppClientRef.current.status !== 'online')) {
          try {
            await xmppClientRef.current.stop();
          } catch {
            // Ignora erros ao parar cliente antigo
          }
          xmppClientRef.current = null;
          clearGlobalXMPPClient();
        }

        const { XMPPSetupService } = await import('@/lib/libs/xmpp/services/xmppSetup.service');

        const xmppConfig = {
          xmppDomain,
          xmppUser,
          xmppPw,
          agentId: squadToken?.temporaryUserId || userId,
        };

        // Configura e inicializa cliente XMPP
        const xmppClient = await XMPPSetupService.setupXMPPClient(xmppConfig);
        xmppClientRef.current = xmppClient;
        connectedIdentityRef.current = requestedIdentity;
        setGlobalXMPPClient(xmppClient);
        window.dispatchEvent(new Event('xmpp-client-changed'));

        // Reset contador de tentativas apÃ³s sucesso
        retryCountRef.current = 0;
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }

        console.log('[XMPPInit] ConexÃ£o XMPP estabelecida com sucesso');
      } catch (error) {
        console.error('[XMPPInit] Erro ao conectar ao XMPP:', error);
        // Limpa ref para permitir retry
        xmppClientRef.current = null;
        connectedIdentityRef.current = null;
        clearGlobalXMPPClient();
        if (retryCountRef.current < MAX_RETRIES && !retryTimerRef.current) {
          const delay = RETRY_BASE_DELAY_MS * retryCountRef.current;
          console.warn(`[XMPPInit] Nova tentativa em ${delay / 1000}s`);
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            setConnectionCheck((previous) => previous + 1);
          }, delay);
        }
      } finally {
        isConnectingRef.current = false;
      }
    };

    initXMPP();

    // NÃƒO retornar cleanup aqui - estÃ¡ separado acima (padrÃ£o do projeto pai)
  }, [userId, isExternal, isLoadingSettings, xmppDomain, xmppUser, xmppPw, squadToken?.temporaryUserId, connectionCheck]);

  return null;
}
